import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Loading } from '../../../global/Loading/Loading'
import { Modal as LegacyModal } from '../../../global/modal/Modal'
import { ScrollToIds } from '../../../hooks/useScrollToElementIds'
import { FileIcon } from '../../../icons/FileIcon'
import { CreateTradingAccountIntroducingBrokerRebateGroupBulk } from '../../../model/CreateTradingAccountIntroducingBrokerRebateGroupBulk'
import { GetUpdateIntroducingBrokerTradingAccountRebateBulk } from '../../../model/GetUpdateIntroducingBrokerTradingAccountRebateBulk'
import { IntroducingBrokerRebateGroupDto } from '../../../model/IntroducingBrokerRebateGroupDto'
import { TradingAccountRebateDetailedDto } from '../../../model/TradingAccountRebateDetailedDto'
import { Text } from '../../../ui/Typography/Typography'
import { Modal } from '../../../ui/v2/Modal'
import { PageSection } from '../../../ui/v2/PageSection/PageSection'
import { Pagination } from '../../../ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '../../../ui/v2/TableFilter/TableFilter'
import { useAccountReadContext } from '../../../utils/AccountContextContext'
import { HeaderActions } from '../Common/HeaderActions'
import { GroupFormValues, GroupModal } from '../GroupModal'
import { RebateOptionsModal } from '../RebateOptionsModal'
import { RebatesSummary } from '../RebatesSummary'
import { useCreateOrUpdateRebateGroup } from '../hooks/useCreateOrUpdateRebateGroup'
import { useIBRebateGroupsSearch } from '../hooks/useIBRebateGroupsSearch'
import { AddToGroupFormValues, AddToGroupModal } from './AddToGroupModal'
import { ButtonActions } from './ButtonActions'
import { ClientsPageTable, EmptyClientsPageTable } from './ClientsPageTable'
import { NoGroupModal } from './NoGroupModal'
import { RebateSettingsFormValues, RebateSettingsModal } from './RebateSettingsModal'
import { useAddIBTradingAccountsRebateGroups } from './hooks/useAddIBTradingAccountsRebateGroups'
import { useIBClientFilterOptions } from './hooks/useIBClientFilterOptions'
import { useIBRebateClientsSummary } from './hooks/useIBRebateClientsSummary'
import { useIBTradingAccountsRebate } from './hooks/useIBTradingAccountsRebate'
import { useResetRebateSettings } from './hooks/useResetRebateSettings'
import { useUpdateIBTradingAccountsRebates } from './hooks/useUpdateIBTradingAccountsRebates'

import styles from './ClientsPage.module.scss'

enum TargetModal {
  GROUP = 'GROUP',
  REBATE_SETTINGS = 'REBATE_SETTINGS',
}

interface OptionsModalData {
  targetModal: TargetModal
}

export const IBRebatesClientsPage: FC = () => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()

  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isRebateSettingsModalOpen, setIsRebateSettingsModalOpen] = useState(false)
  const [isAddToGroupModalOpen, setIsAddToGroupModalOpen] = useState(false)
  const [isNoGroupModalOpen, setIsNoGroupModalOpen] = useState(false)
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false)
  const [approvalTypeModalData, setApprovalTypeModalData] = useState<OptionsModalData | null>()
  const [periodModalData, setPeriodModalData] = useState<OptionsModalData | null>()
  const [isRebateChangeModalOpen, setIsRebateChangeModalOpen] = useState(false)
  const [isRebateGroupOptionsModalOpen, setIsRebateGroupOptionsModalOpen] = useState(false)
  const [selectedRebateGroupOption, setSelectedRebateGroupOption] =
    useState<Partial<AddToGroupFormValues>>()
  const [resetClient, setResetClient] = useState<TradingAccountRebateDetailedDto>()
  const [selectedGroup, setSelectedGroup] = useState<Partial<IntroducingBrokerRebateGroupDto>>()
  const [selectedRebateSettings, setSelectedRebateSettings] =
    useState<Partial<RebateSettingsFormValues>>()
  const [isCreatingNewGroup, setIsCreatingNewGroup] = useState(false)

  const activeApprovalTypeId = useMemo<number | undefined>(() => {
    if (approvalTypeModalData?.targetModal === TargetModal.GROUP) {
      return selectedGroup?.rebateApprovalType?.id
    }

    if (approvalTypeModalData?.targetModal === TargetModal.REBATE_SETTINGS) {
      return selectedRebateSettings?.rebateApprovalTypeId
    }

    return undefined
  }, [
    selectedGroup?.rebateApprovalType?.id,
    selectedRebateSettings?.rebateApprovalTypeId,
    approvalTypeModalData,
  ])

  const activePeriodId = useMemo<number | undefined>(() => {
    if (periodModalData?.targetModal === TargetModal.GROUP) {
      return selectedGroup?.rebatePeriod?.id
    }

    if (periodModalData?.targetModal === TargetModal.REBATE_SETTINGS) {
      return selectedRebateSettings?.rebatePeriodId
    }

    return undefined
  }, [selectedGroup?.rebatePeriod?.id, selectedRebateSettings?.rebatePeriodId, periodModalData])

  const {
    query: tableQuery,
    sortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
    forceRefresh: forceRefreshAccounts,
  } = useIBTradingAccountsRebate()

  const {
    data: summaryItems,
    forceRefresh: refreshSummaryItems,
    isLoading: isSummaryLoading,
  } = useIBRebateClientsSummary(tableSearch)

  const isAnyFilterActive = useMemo(
    () => Object.values(tableSearch || {}).some((value) => !!value),
    [tableSearch]
  )

  const isTableLoading = tableQuery.isLoading

  const {
    accountTypes,
    tradingAccounts,
    clients,
    groups: groupFilterOptions,
    signupDateShortcutOptions,
    countries,
    rebateApprovalTypes,
    rebatePeriods,
  } = useIBClientFilterOptions()

  const { groupOptions: allGroupOptions, query: groupsQuery } = useIBRebateGroupsSearch({
    query: { pageSize: 1 },
  })

  const refreshData = () => {
    forceRefreshAccounts()
    refreshSummaryItems()
    setSelectedIds([])
  }

  const { resetRebateSettings } = useResetRebateSettings({
    onCompleted: () => {
      forceRefreshAccounts()
      setResetClient(undefined)
    },
  })

  const { updateRebates } = useUpdateIBTradingAccountsRebates({
    onCompleted: refreshData,
  })

  const { addToGroups } = useAddIBTradingAccountsRebateGroups({
    onCompleted: refreshData,
  })

  const { createOrUpdateRebateGroup } = useCreateOrUpdateRebateGroup()

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const handleCheckBoxClick = () => {
    if (selectedIds.length > 0) {
      setSelectedIds([])

      return
    }

    setSelectedIds(tableQuery.data.map((item) => item.tradingAccount.id))
  }

  const openAddToGroupModal = () => {
    groupsQuery.setPageQuery?.({
      ...groupsQuery.pageQuery,
      pageSize: 999,
    })

    setIsAddToGroupModalOpen(true)
  }

  const openRebateGroupOptionsModal = () => {
    groupsQuery.setPageQuery?.({
      ...groupsQuery.pageQuery,
      pageSize: 999,
    })

    setIsRebateGroupOptionsModalOpen(true)
  }

  const handleAddToGroupClick = () => {
    if (!allGroupOptions?.length) {
      setIsNoGroupModalOpen(true)
      return
    }

    openAddToGroupModal()
  }

  const handleEditClick = (client: TradingAccountRebateDetailedDto) => {
    setSelectedIds([client.tradingAccount.id])
    setSelectedRebateSettings({
      rebateAmountInPercentage: client?.rebateAmountInPercentage
        ? String(client.rebateAmountInPercentage)
        : undefined,
      rebateApprovalTypeId: client.rebateApprovalType?.id ?? undefined,
      rebatePeriodId: client.rebatePeriod?.id ?? undefined,
    })
    setIsRebateSettingsModalOpen(true)
  }

  const handleAddToGroupModalClose = () => {
    setIsAddToGroupModalOpen(false)
    setSelectedRebateGroupOption(undefined)
    setSelectedIds([])
  }

  const handleRebateSettingsModalClose = () => {
    setIsRebateSettingsModalOpen(false)
    setSelectedRebateSettings(undefined)
    setSelectedIds([])
  }

  const handleRebateSettingsSubmit = async (values: Partial<RebateSettingsFormValues>) => {
    if (
      values.rebateApprovalTypeId === undefined ||
      values.rebatePeriodId === undefined ||
      !values.rebateAmountInPercentage ||
      !account?.clientIntroducingBroker?.id ||
      selectedIds.length === 0
    ) {
      return
    }

    const payload: GetUpdateIntroducingBrokerTradingAccountRebateBulk = {
      tradingAccountIds: selectedIds,
      rebateApprovalTypeId: values.rebateApprovalTypeId,
      rebatePeriodId: values.rebatePeriodId,
      rebateAmountInPercentage: parseFloat(values.rebateAmountInPercentage),
      introducingBrokerId: account.clientIntroducingBroker.id,
    }

    const response = await updateRebates(payload)

    setSelectedRebateSettings(undefined)

    setIsRebateSettingsModalOpen(false)

    if (response?.skipped.length === 0) {
      const hasManualApproval = selectedIds.some((id) => {
        const client = tableQuery.data.find((item) => item.tradingAccount.id === id)
        return client?.rebateApprovalType?.id === 1
      })

      if (hasManualApproval && values.rebateApprovalTypeId === 2) {
        setIsRebateChangeModalOpen(true)
      }
    }
  }

  const handleAddToGroupSubmit = async (values: Partial<AddToGroupFormValues>) => {
    if (!values.groupId || !account?.clientIntroducingBroker?.id || selectedIds.length === 0) {
      return
    }

    const payload: CreateTradingAccountIntroducingBrokerRebateGroupBulk = {
      tradingAccountIds: selectedIds,
      groupId: values.groupId,
      introducingBrokerId: account.clientIntroducingBroker.id,
    }

    await addToGroups(payload)
    setIsAddToGroupModalOpen(false)
    setSelectedRebateGroupOption(undefined)
    setSelectedIds([])
  }

  const handleCreateGroupClick = () => {
    setIsNoGroupModalOpen(false)
    setSelectedGroup(undefined)
    setIsCreatingNewGroup(true)
    setIsCreateGroupModalOpen(true)
  }

  const handleCreateGroupSubmit = async (values: Partial<GroupFormValues>) => {
    if (
      values.name &&
      values.rebateAmountInPercentage &&
      values.rebateApprovalTypeId &&
      values.rebatePeriodId
    ) {
      await createOrUpdateRebateGroup({
        name: values.name,
        rebateAmountInPercentage: values.rebateAmountInPercentage,
        rebateApprovalTypeId: values.rebateApprovalTypeId,
        rebatePeriodId: values.rebatePeriodId,
        isDefault: !!values.isDefault,
      })
    }

    handleAddToGroupModalClose()
    refreshData()
  }

  return (
    <>
      {isCreateGroupModalOpen && !approvalTypeModalData && !periodModalData && (
        <LegacyModal
          pullDown
          closeModal={() => setIsCreateGroupModalOpen(false)}
          render={() => (
            <GroupModal
              closeModal={() => {
                setIsCreateGroupModalOpen(false)
                setSelectedGroup(undefined)
                setIsCreatingNewGroup(false)
              }}
              onSubmit={handleCreateGroupSubmit}
              approvalTypes={rebateApprovalTypes}
              rebatePeriods={rebatePeriods}
              groupData={selectedGroup}
              isCreateGroup={isCreatingNewGroup}
              onOpenRebateApprovalTypeModal={() => {
                setApprovalTypeModalData({
                  targetModal: TargetModal.GROUP,
                })
              }}
              onOpenRebatePeriodModal={() => {
                setPeriodModalData({
                  targetModal: TargetModal.GROUP,
                })
              }}
              onSetSelectedGroup={(selectedGroup: Partial<IntroducingBrokerRebateGroupDto>) =>
                setSelectedGroup(selectedGroup)
              }
            />
          )}
        />
      )}
      {!!approvalTypeModalData && (
        <RebateOptionsModal
          options={rebateApprovalTypes}
          activeId={activeApprovalTypeId}
          onSelect={(option) => {
            if (approvalTypeModalData.targetModal === TargetModal.GROUP) {
              setSelectedGroup((selectedGroup) => ({
                ...selectedGroup,
                rebateApprovalType: option,
              }))
            }
            if (approvalTypeModalData.targetModal === TargetModal.REBATE_SETTINGS && option.id) {
              setSelectedRebateSettings((selectedRebateSettings) => ({
                ...selectedRebateSettings,
                rebateApprovalTypeId: option.id,
              }))
            }
            setApprovalTypeModalData(null)
          }}
          onClose={() => setApprovalTypeModalData(null)}
        />
      )}
      {!!periodModalData && (
        <RebateOptionsModal
          options={rebatePeriods}
          activeId={activePeriodId}
          onSelect={(option) => {
            if (periodModalData.targetModal === TargetModal.GROUP) {
              setSelectedGroup((selectedGroup) => ({ ...selectedGroup, rebatePeriod: option }))
            }
            if (periodModalData.targetModal === TargetModal.REBATE_SETTINGS && option.id) {
              setSelectedRebateSettings((selectedRebateSettings) => ({
                ...selectedRebateSettings,
                rebatePeriodId: option.id,
              }))
            }
            setPeriodModalData(null)
          }}
          onClose={() => setPeriodModalData(null)}
        />
      )}
      {isNoGroupModalOpen && (
        <LegacyModal
          pullDown
          closeModal={() => setIsNoGroupModalOpen(false)}
          render={() => (
            <NoGroupModal
              closeModal={() => setIsNoGroupModalOpen(false)}
              onCreate={() => {
                setIsNoGroupModalOpen(false)
                setSelectedGroup(undefined)
                setIsCreatingNewGroup(true)
                setIsCreateGroupModalOpen(true)
              }}
            />
          )}
        />
      )}
      {isAddToGroupModalOpen && !isRebateGroupOptionsModalOpen && (
        <LegacyModal
          pullDown
          closeModal={handleAddToGroupModalClose}
          render={() => (
            <AddToGroupModal
              closeModal={handleAddToGroupModalClose}
              onSubmit={handleAddToGroupSubmit}
              initialValues={selectedRebateGroupOption}
              groupsQuery={groupsQuery}
              groupOptions={allGroupOptions}
              openGroupSelectionModal={() => openRebateGroupOptionsModal()}
            />
          )}
        />
      )}
      {isRebateGroupOptionsModalOpen && (
        <LegacyModal
          pullDown
          closeModal={() => setIsRebateGroupOptionsModalOpen(false)}
          render={() => (
            <RebateOptionsModal<string>
              options={allGroupOptions}
              isLoading={groupsQuery.isLoading}
              activeId={selectedRebateGroupOption?.groupId}
              onSelect={(option) => {
                setSelectedRebateGroupOption({
                  groupId: option.id,
                })
                setIsRebateGroupOptionsModalOpen(false)
              }}
              onClose={() => setIsRebateGroupOptionsModalOpen(false)}
            />
          )}
        />
      )}
      {isRebateSettingsModalOpen && !approvalTypeModalData && !periodModalData && (
        <LegacyModal
          pullDown
          closeModal={handleRebateSettingsModalClose}
          render={() => (
            <RebateSettingsModal
              closeModal={handleRebateSettingsModalClose}
              onSubmit={handleRebateSettingsSubmit}
              approvalTypes={rebateApprovalTypes}
              rebatePeriods={rebatePeriods}
              initialValues={selectedRebateSettings}
              onOpenRebateApprovalTypeModal={(values) => {
                setSelectedRebateSettings(values)
                setApprovalTypeModalData({
                  targetModal: TargetModal.REBATE_SETTINGS,
                })
              }}
              onOpenRebatePeriodModal={(values) => {
                setSelectedRebateSettings(values)
                setPeriodModalData({
                  targetModal: TargetModal.REBATE_SETTINGS,
                })
              }}
            />
          )}
        />
      )}
      {isRebateChangeModalOpen && (
        <Modal.Info
          title={t('IB.RebatesPage.ClientsPage.Rebate type change')}
          onClose={() => setIsRebateChangeModalOpen(false)}
        >
          <Text>
            {t(
              'IB.RebatesPage.ClientsPage.You’ve changed rebate payout type. It will be reflected in all future payouts. Current and/or previous payouts for approvals will remain as is in Approvals section'
            )}
          </Text>
        </Modal.Info>
      )}
      {!!resetClient && (
        <Modal.Confirm
          title={t('Please, confirm your action')}
          onClose={() => setResetClient(undefined)}
          onConfirm={() => resetRebateSettings(resetClient)}
          isValid
        >
          <Text>{t('IB.RebatesPage.ClientsPage.You are about to reset Rebate setting')}</Text>
        </Modal.Confirm>
      )}
      <div className={styles.clientsPage}>
        <RebatesSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />
        <div className={styles.container}>
          <PageSection
            icon={FileIcon}
            title={t('IB.RebatesPage.Trading accounts')}
            id={ScrollToIds.IBRebateClientsHeader}
          >
            <HeaderActions onFiltersClick={() => setIsFilterVisible((prevState) => !prevState)} />
          </PageSection>
          <TableFilter
            isVisible={isFilterVisible}
            searchFilters={tableSearch}
            tradingAccountTypeOptions={accountTypes}
            dateShortcutOptions={signupDateShortcutOptions}
            countryOptions={countries}
            showRebateCheckbox
            tradingAccountSearchProps={{
              options: tradingAccounts,
            }}
            clientsSearchProps={{
              options: clients,
            }}
            groupSearchProps={{
              options: groupFilterOptions,
            }}
            filterOptions={[
              FilterOptions.TradingAccountId,
              FilterOptions.ClientId,
              FilterOptions.TradingAccountType,
              FilterOptions.SignedUpDate,
              FilterOptions.Country,
              FilterOptions.RebateGroupId,
              FilterOptions.VolumeFxm,
              FilterOptions.VolumeOther,
              FilterOptions.TotalCommission,
              FilterOptions.RebateAmount,
            ]}
            customLabels={{
              [FilterOptions.RebateGroupId]: t('IB.RebatesPage.ClientsPage.Group'),
            }}
            setSearchFilters={(searchFilters) => {
              if (!searchFilters) {
                return
              }
              setTableSearch(searchFilters)
              tableQuery.setPageQuery?.({
                ...tableQuery.pageQuery,
                pageIndex: 1,
              })
            }}
          />
          <ButtonActions
            isCheckBoxActive={selectedIds.length > 0}
            isCheckBoxDisabled={isTableLoading || !tableQuery.data.length}
            onCheckBoxClick={handleCheckBoxClick}
            onEditClick={() => setIsRebateSettingsModalOpen(true)}
            onAddToGroupClick={handleAddToGroupClick}
            isDisabled={selectedIds.length === 0}
          />
          <div className={styles.tableContainer}>
            <Loading isLoading={isTableLoading} showLoadingIcon>
              {tableQuery.data.length ? (
                <ClientsPageTable
                  tableData={tableQuery.data}
                  sortOptions={sortOptions}
                  pageQuery={tableQuery.pageQuery}
                  sticky='left'
                  setPageQuery={tableQuery.setPageQuery}
                  onSelect={setSelectedIds}
                  selectedIds={selectedIds}
                  onEdit={handleEditClick}
                  onReset={(client) => setResetClient(client)}
                />
              ) : (
                <EmptyClientsPageTable isAnyFilterActive={isAnyFilterActive} />
              )}
              {tableQuery.meta && tableQuery.hasInitialResults && (
                <Pagination
                  scrollToHeaderId={ScrollToIds.IBRebateClientsHeader}
                  maxPageSize={100}
                  pageData={tableQuery.meta}
                  isLoading={tableQuery.isLoading}
                  onPageChanged={handlePageChange}
                />
              )}
            </Loading>
          </div>
        </div>
      </div>
    </>
  )
}
